import { Box } from '@chakra-ui/react'
import { createContext, useContext, useEffect, useRef, useState} from 'react'
import { paintTrail } from '../../../sharedF/canvasF/paintTrailF.mts'
import * as _ from 'lodash-es'
import { useEvtListen } from '../../Chakra3F/useEventListenerMapF'
import { renderFinishedTrails } from '../../../sharedF/canvasF/renderFinishedTrailsF.mts'

let isMouseDown = false

const getTouchProps = ({canvasRef, paintPoint, finishPaint, width}) => {

  const evtToPt = (evt) => {
    const canvasEl = canvasRef.current
    const rect = canvasEl.getBoundingClientRect()
    const ratio = width / rect.width
    const touch = evt.touches[0]
    const touchCoord = [
      Math.round((touch.clientX - rect.x) * ratio),
      Math.round((touch.clientY - rect.y) * ratio),
    ]
    return touchCoord
  }

  return {
    onTouchStart: (evt) => {
      paintPoint(evtToPt(evt))
      evt.preventDefault()
    },
    onTouchMove: (evt) => {
      paintPoint(evtToPt(evt))
      evt.preventDefault()
    },
    onTouchEnd: (evt) => {
      finishPaint()
      evt.preventDefault()
    }
  }
}

const getMouseProps = ({canvasRef, paintPoint, finishPaint, width}) => {
  const evtToPt = (evt) => {
    const canvasEl = canvasRef.current
    const rect = canvasEl.getBoundingClientRect()
    const ratio = width / rect.width
    const {clientX, clientY} = evt
    // console.log({rect, clientX, clientY})
    const pt = [
      Math.round((clientX - rect.x) * ratio),
      Math.round((clientY - rect.y) * ratio),
    ]
    const isInRegion = _.inRange(clientX, rect.left, rect.right) && _.inRange(clientY, rect.top, rect.bottom)
    return {pt, isInRegion}
  }

  return {
    mousedown: (evt) => {
      isMouseDown = true
      const {pt, isInRegion} = evtToPt(evt)
      if (!isInRegion) return
      paintPoint(pt)
    },
    mousemove: (evt) => {
      if (!isMouseDown) return
      const {pt, isInRegion} = evtToPt(evt)
      if (!isInRegion) return
      paintPoint(pt)
    },
    mouseup: (evt) => {
      if (!isMouseDown) return
      const {pt, isInRegion} = evtToPt(evt)
      if (isInRegion) {
        paintPoint(pt)
      }
      finishPaint()
      isMouseDown = false
    },
  }
}

/** @typedef {import('../../../sharedF/canvasF/renderFinishedTrailsF.mts').Trail} CanvasTrail */

/**
 * @typedef {object} CanvasDrawCtxShape
 * @property {boolean} readonly
 * @property {import('react').RefObject<HTMLCanvasElement | null>} canvasRef
 * @property {number} usedPct
 * @property {(newCoord: [number, number]) => [number, number][]} paintPoint
 * @property {() => void} finishPaint
 * @property {() => void} undoDraw
 * @property {() => void} clearDrawing
 * @property {number} width
 * @property {number} height
 * @property {object} [canvasStyle]
 * @property {string} [budgetAction]
 * @property {string} activeColor
 * @property {(color: string) => void} setActiveColor
 * @property {CanvasTrail[]} finishedTrails
 * @property {[number, number][]} paintedPoints
 * @property {number} cursorSize
 * @property {(size: number) => void} setCursorSize
 * @property {import('react').ReactNode} [showBudget]
 * @property {import('react').ComponentType} [DrawControlsComp]
 * @property {(props: {ctx: CanvasRenderingContext2D | null}) => void} [redrawFnc]
 * @property {(props: {ctx: CanvasRenderingContext2D | null}) => void} [redrawFncAfter]
 * @property {any} [redrawKey]
 */
export const CanvasDrawContext = createContext(/** @type {CanvasDrawCtxShape} */ ({}))

const truncateTrails = ({finishedTrails, paintedPoints, pixelCount, pixelBudget}) => {
  finishedTrails = _.cloneDeep(finishedTrails)
  let toRemove = pixelCount - pixelBudget
  while (toRemove >= 0) {
    let firstTrail = finishedTrails[finishedTrails.length - 1]
    if (!firstTrail) {
      break // ???
    }
    const removedPt = firstTrail.points.pop()
    if (removedPt) {
      toRemove = toRemove - 1
    } else {
      finishedTrails.pop()
    }
  }
  while (toRemove >= 0) {
    paintedPoints.pop()
    toRemove = toRemove - 1
  }
  return {finishedTrails, paintedPoints}
}

export const CanvasDraw = (/** @type {{readonly: boolean, width: number, height: number, finishedTrails: CanvasTrail[], setFinishedTrails: (trails: CanvasTrail[]) => void, initCursorColor?: string, initCursorSize?: number, pixelBudget: number, budgetAction?: string, [key: string]: any}} */ {readonly, width, height, finishedTrails, setFinishedTrails, initCursorColor, initCursorSize, pixelBudget, budgetAction, ...props}) => {
  const canvasRef = useRef(/** @type {HTMLCanvasElement | null} */ (null))

  let [paintedPoints, setPaintedPoints] = useState(/** @type {[number, number][]} */ ([]))

  const pixelCount = _.flatten(_.map(finishedTrails, 'points')).length + paintedPoints.length
  if (budgetAction == 'truncate') {
    ;({finishedTrails, paintedPoints} = truncateTrails({finishedTrails, paintedPoints, pixelCount, pixelBudget}))
  }

  const usedPct = Math.min(pixelCount / pixelBudget, 1.0)

  const paintPoint = (newCoord) => {
    const ret = _.union([newCoord], paintedPoints)
    setPaintedPoints(ret)
    return ret
  }
  const [activeColor, setActiveColor] = useState(initCursorColor || 'black')
  const [cursorSize, setCursorSize] = useState(initCursorSize || 3)

  const finishPaint = () => {
    if (paintedPoints.length == 0) return
    const trail = {points: paintedPoints, color: activeColor, cursorSize}
    setFinishedTrails(_.union([trail], finishedTrails))
    setPaintedPoints([])
  }

  const undoDraw = () => {
    setFinishedTrails(_.slice(finishedTrails, 1))
  }
  const clearDrawing = () => {
    setFinishedTrails([])
  }

  const value = {
    ...props,
    readonly, width, height, finishedTrails, setFinishedTrails,
    budgetAction,
    undoDraw, clearDrawing,
    cursorSize, setCursorSize,
    finishPaint, usedPct,
    activeColor, setActiveColor,
    canvasRef, paintedPoints, paintPoint, setPaintedPoints,
  }

  const children = <CanvasDrawInner />
  return <CanvasDrawContext.Provider {...{children, value}} />
}

const CanvasComp = () => {
  const {
    readonly, canvasRef, usedPct,
    paintPoint, finishPaint,
    width, height,
    canvasStyle, budgetAction
  } = useContext(CanvasDrawContext)

  const style = {
    touchAction: 'none',
    height: '100%', width: '100%',
    ...canvasStyle
  }

  const canvasProps = {
    width, height, ref: canvasRef, style
  }

  const isTouchDevice = /** @type {{browser?: boolean}} */ (process).browser && ('ontouchstart' in document.documentElement)

  if (readonly || budgetAction == 'stop' && usedPct >= 1.0) {
    Object.assign(style, {
      pointerEvents: 'none',
    })
  } else if (isTouchDevice) {
    Object.assign(canvasProps, getTouchProps({canvasRef, paintPoint, finishPaint, width}))
  } else {
    const eventsH = getMouseProps({canvasRef, paintPoint, finishPaint, width})
    for(const key in eventsH) {
      useEvtListen(key, eventsH[key])
    }
  }

  return <canvas {...canvasProps} />
}

const CanvasDrawInner = () => {
  const {
    readonly, activeColor, finishedTrails, canvasRef,
    paintedPoints, cursorSize, usedPct,
    showBudget, DrawControlsComp, redrawFnc, redrawFncAfter, redrawKey,
    width, height,
  } = useContext(CanvasDrawContext)

  useEffect(() => {
    const canvasEl = canvasRef.current
    if (!canvasEl) return () => {}

    const ctx = canvasEl.getContext('2d')
    if (!ctx) return () => {}

    ctx.clearRect(0, 0, width, height)
    redrawFnc?.({ctx})
  
    renderFinishedTrails({ctx, finishedTrails})
    paintTrail({ctx, trail: {points: paintedPoints, color: activeColor, cursorSize}})
  
    redrawFncAfter?.({ctx})

    return () => {}
  }, [canvasRef, finishedTrails.length, paintedPoints, redrawKey])

  return <>
    <Box {...{userSelect: 'none', w: '100%'}}>
      {!readonly && showBudget}
      <CanvasComp />
    </Box>
    {!readonly && DrawControlsComp && <DrawControlsComp />}
  </>
}